// Himmel, Licht, Nebel, Wetter. Konzept §12.1 Punkt 4: bestes Verhältnis
// von Atmosphäre zu Arbeit — derselbe Korridor bei Nebel, Gegenlicht oder
// Regen ist gefühlt eine andere Strecke.
//
// Erstes System in der Update-Reihenfolge: setzt die Weltzeit, nach der
// alle anderen ticken. Offline startet die lokale Uhr (clockOffset = 0).

import * as THREE from 'three';
import { sunElevation, nowWorldSeconds } from '../../shared/worldtime.js';
import { fbm1 } from '../../shared/rng.js';
import { WORLD_SEED } from '../../shared/constants.js';
import { STATIONS, trackPoint } from '../../shared/track.js';
import { PALETTE, timeOfDayColors, STYLE_RULES } from '../render/style.js';

// Wiederverwendete Vektoren — nichts pro Frame allokieren (CONTRACT).
const _sunDir = new THREE.Vector3();
const _lightPos = new THREE.Vector3();
const _focus = new THREE.Vector3();
const _moonDir = new THREE.Vector3();
const _cSky = new THREE.Color();
const _cHorizon = new THREE.Color();
const _cFog = new THREE.Color();
const _cSun = new THREE.Color();
const _cGround = new THREE.Color(PALETTE.sandDark);
const _cHemiSky = new THREE.Color();

const SKY_RADIUS = 2800;
const STAR_COUNT = 480;
const RAIN_COUNT = 900;
const RAIN_RADIUS = 22;
const RAIN_HEIGHT = 18;

// Wetter-Seeds: getrennte Achsen, damit Regen und Wind nicht synchron wogen.
const SEED_RAIN = WORLD_SEED ^ 0x7a17;
const SEED_FOG = WORLD_SEED ^ 0xf0a1;
const SEED_WIND = WORLD_SEED ^ 0x1d1e;

export class SkySystem {
  async init(ctx) {
    this.group = new THREE.Group();
    this.group.name = 'sky';
    ctx.scene.add(this.group);

    this._buildDome(ctx);
    this._buildStars(ctx);
    this._buildLights(ctx);
    this._buildRain(ctx);

    // Fog einmal anlegen; Farben/Distanzen werden pro Frame geschrieben.
    ctx.scene.fog = new THREE.Fog(PALETTE.fogDay, 80, 800);
    // Eigene Hintergrundfarbe — darf nicht dieselbe Color-Instanz wie fog.color
    // sein, sonst verschwindet der Horizont (Milchschleier).
    this._bgColor = new THREE.Color(PALETTE.fogDay);

    // Smoothing-Zustand: fbm ist stetig, aber wetterbedingter Nebel-Boost
    // und Intensitäten glätten wir leicht, damit ein 10-Minuten-Dämmerungs-
    // sitzen keine Stufen zeigt (Konzept: die Fahrt muss tragen).
    this._smooth = { rain: 0, fog: 0, wind: 0.3 };
    this._sun = { elevation: 0, colors: null, direction: _sunDir };

    // Werkzeug-Overrides (shoot.js): null = normale Server-/Lokalzeit.
    this._debugWorldSeconds = null;
    this._debugWeather = null;

    // Erster Tick, damit andere Systeme in init() schon Weltzeit sehen.
    this.update(0, ctx);
  }

  /**
   * Debug/Werkzeug-API: Weltzeit und Wetter festnageln.
   * Nur für Sichtprüfungen (scripts/shoot.js) — nicht fürs normale Spiel.
   * Wetter snapt sofort (kein Smoothing), damit ein Frame genügt.
   * Felder mit `null` geben den jeweiligen Override frei.
   */
  setDebugOverride({ worldSeconds, weather } = {}) {
    if (worldSeconds !== undefined) this._debugWorldSeconds = worldSeconds;
    if (weather !== undefined) {
      this._debugWeather = weather;
      if (weather) {
        if (weather.rain != null) this._smooth.rain = weather.rain;
        if (weather.fog != null) this._smooth.fog = weather.fog;
        if (weather.wind != null) this._smooth.wind = weather.wind;
      }
    }
  }

  update(dt, ctx) {
    // Serverkorrigiert wenn verbunden, sonst lokaler Offset 0 → offline-fähig.
    // Debug-Override (shoot.js) gewinnt, sonst wäre jeder Shot serverzeit-abhängig.
    const ws = this._debugWorldSeconds != null
      ? this._debugWorldSeconds
      : (ctx.net?.worldSeconds?.() ?? nowWorldSeconds(0));
    ctx.worldSeconds = ws;

    const elevation = sunElevation(ws);
    const colors = timeOfDayColors(elevation);
    this._sun.elevation = elevation;
    this._sun.colors = colors;
    this._computeSunDir(elevation, ws, _sunDir);
    this._sun.direction = _sunDir;
    ctx.sun = this._sun;

    // --- Wetter ---
    let rain;
    let fogW;
    let wind;
    if (this._debugWeather) {
      // Feste Werte für reproduzierbare Screenshots.
      rain = this._debugWeather.rain ?? 0;
      fogW = this._debugWeather.fog ?? 0;
      wind = this._debugWeather.wind ?? 0.3;
      this._smooth.rain = rain;
      this._smooth.fog = fogW;
      this._smooth.wind = wind;
    } else {
      // Deterministisch aus Weltzeit, geteilt für alle Spieler.
      // Langsame fbm1-Wanderung: ~mehrere Spielstunden für einen Wetterwechsel.
      const hours = ws / 3600;
      const rainT = clamp01((fbm1(hours * 0.045, SEED_RAIN, 3) - 0.25) * 1.35);
      const fogT = clamp01((fbm1(hours * 0.03 + 17.3, SEED_FOG, 3) + 0.15) * 0.7);
      const windT = clamp01(fbm1(hours * 0.08 + 3.1, SEED_WIND, 2) * 0.5 + 0.5);

      // Exponentielles Nachziehen: verhindert Sprünge bei Tab-Wechsel.
      const k = 1 - Math.exp(-Math.min(dt, 0.1) * 1.8);
      this._smooth.rain += (rainT - this._smooth.rain) * k;
      this._smooth.fog += (fogT - this._smooth.fog) * k;
      this._smooth.wind += (windT - this._smooth.wind) * k;

      // Regen treibt Nebel leicht mit — nass und diesig gehören zusammen.
      rain = this._smooth.rain;
      fogW = clamp01(this._smooth.fog + rain * 0.35);
      wind = this._smooth.wind;
    }
    ctx.weather = { rain, fog: fogW, wind };

    this._applySky(colors, _sunDir);
    this._applyLights(ctx, colors, elevation, _sunDir);
    this._applyFog(ctx, colors, fogW);
    this._applyExposure(ctx, colors);
    this._applyStars(colors.dayness);
    this._applyRain(ctx, rain, wind, dt);
  }

  // --- Aufbau ---------------------------------------------------------------

  _buildDome(ctx) {
    // Invertierte Kugel: wir sitzen drin. Stilisierter Gradient (§12.4),
    // kein Physical-Sky — billiger und zum Look passender.
    const geo = new THREE.SphereGeometry(SKY_RADIUS, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      uniforms: {
        uSky: { value: new THREE.Color(PALETTE.skyDay) },
        uHorizon: { value: new THREE.Color(PALETTE.horizonDay) },
        uSunColor: { value: new THREE.Color(PALETTE.sunDay) },
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },
        uSunGlow: { value: 1.0 },
      },
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          // Richtung vom Zentrum — Kugel ist kamerafest (Position folgt Kamera).
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uSky;
        uniform vec3 uHorizon;
        uniform vec3 uSunColor;
        uniform vec3 uSunDir;
        uniform float uSunGlow;
        varying vec3 vDir;
        void main() {
          vec3 dir = normalize(vDir);
          // Höhenblend: Horizont warm/hell, Zenit kühler.
          float h = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);
          float t = pow(h, 0.65);
          vec3 col = mix(uHorizon, uSky, t);

          // Weiches Sonnenglühen um die Sonnenrichtung (auch als Mond nachts).
          float sunDot = max(0.0, dot(dir, normalize(uSunDir)));
          float glow = pow(sunDot, 32.0) * 0.55 * uSunGlow;
          float halo = pow(sunDot, 4.0) * 0.12 * uSunGlow;
          col += uSunColor * (glow + halo);

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    this.dome = new THREE.Mesh(geo, mat);
    this.dome.frustumCulled = false;
    this.dome.renderOrder = -1000;
    this.group.add(this.dome);
    this.domeUniforms = mat.uniforms;
  }

  _buildStars(ctx) {
    const positions = new Float32Array(STAR_COUNT * 3);
    const sizes = new Float32Array(STAR_COUNT);
    // Deterministische Verteilung auf der Halbkugel — gleiche Sterne für alle.
    for (let i = 0; i < STAR_COUNT; i++) {
      // Fibonacci-ähnliche Spirale, nur obere Hemisphäre.
      const y = (i + 0.5) / STAR_COUNT; // 0..1
      const elev = Math.pow(y, 0.7) * 0.92 + 0.05; // meidet Horizont
      const az = i * 2.399963; // goldener Winkel
      const r = Math.sqrt(Math.max(0, 1 - elev * elev));
      const R = SKY_RADIUS * 0.92;
      positions[i * 3] = Math.cos(az) * r * R;
      positions[i * 3 + 1] = elev * R;
      positions[i * 3 + 2] = Math.sin(az) * r * R;
      sizes[i] = 1.2 + ((i * 17) % 10) * 0.15;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));

    const mat = new THREE.PointsMaterial({
      color: PALETTE.moonNight,
      size: 2.4,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      fog: false,
    });
    this.stars = new THREE.Points(geo, mat);
    this.stars.frustumCulled = false;
    this.stars.renderOrder = -999;
    this.group.add(this.stars);
  }

  _buildLights(ctx) {
    // Sonne/Mond: eine DirectionalLight, Schatten nur im Nahbereich.
    // shadowDistance 90 m — weiter wäre Matsch und teuer (STYLE_RULES).
    this.sunLight = new THREE.DirectionalLight(PALETTE.sunDay, 1);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(2048, 2048);
    this.sunLight.shadow.bias = -0.00025;
    this.sunLight.shadow.normalBias = 0.035;
    const cam = this.sunLight.shadow.camera;
    const d = STYLE_RULES.shadowDistance;
    cam.left = -d;
    cam.right = d;
    cam.top = d;
    cam.bottom = -d;
    cam.near = 1;
    cam.far = d * 3.5;
    cam.updateProjectionMatrix();
    // Target folgt dem Spieler (unten in _applyLights).
    this.sunLight.target.position.set(0, 0, 0);
    ctx.scene.add(this.sunLight);
    ctx.scene.add(this.sunLight.target);

    this.hemi = new THREE.HemisphereLight(PALETTE.skyDay, PALETTE.sandDark, 0.4);
    ctx.scene.add(this.hemi);
  }

  _buildRain(ctx) {
    // Gestreckte Punkte um die Kamera: billig, lesbar als Regensträhnen.
    const positions = new Float32Array(RAIN_COUNT * 3);
    // Start-Offsets im Einheitswürfel, werden pro Frame in die Kammer transformiert.
    this._rainOffset = new Float32Array(RAIN_COUNT * 3);
    for (let i = 0; i < RAIN_COUNT; i++) {
      this._rainOffset[i * 3] = (hash01(i, 1) - 0.5) * 2;
      this._rainOffset[i * 3 + 1] = hash01(i, 2);
      this._rainOffset[i * 3 + 2] = (hash01(i, 3) - 0.5) * 2;
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this._rainPos = geo.attributes.position;

    const mat = new THREE.PointsMaterial({
      color: PALETTE.horizonDay,
      size: 0.35,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      // Leicht bläulich-grau über die Materialfarbe; Palette bleibt Quelle.
      fog: true,
    });
    this.rain = new THREE.Points(geo, mat);
    this.rain.frustumCulled = false;
    this.rain.visible = false;
    ctx.scene.add(this.rain);

    this._rainFall = new Float32Array(RAIN_COUNT);
    for (let i = 0; i < RAIN_COUNT; i++) this._rainFall[i] = hash01(i, 4);
  }

  // --- Pro-Frame-Anwendung --------------------------------------------------

  _computeSunDir(elevation, worldSeconds, out) {
    // elevation ist sin-ähnlich (−1..1). Azimut wandert Ost→West über den Tag.
    const elevAngle = Math.asin(clamp(elevation, -1, 1));
    const daySec = ((worldSeconds % 86400) + 86400) % 86400;
    const phase = daySec / 86400;
    // 0.25 = 06:00: Sonne im Osten. phase 0.5 (Mittag) → Süden.
    const azimuth = (phase - 0.25) * Math.PI * 2;
    const ce = Math.cos(elevAngle);
    out.set(
      Math.sin(azimuth) * ce,
      Math.sin(elevAngle),
      Math.cos(azimuth) * ce
    );
    if (out.lengthSq() < 1e-8) out.set(0, 1, 0);
    else out.normalize();

    // Mond ungefähr gegenüber. Weicher Übergang um den Horizont, damit
    // Licht- und Glow-Richtung in der Dämmerung nicht umspringen.
    _moonDir.set(-out.x * 0.85, Math.max(0.08, -out.y * 0.6 + 0.25), -out.z * 0.85);
    _moonDir.normalize();
    const toMoon = clamp01((-elevation) / 0.12);
    if (toMoon > 0) {
      out.lerp(_moonDir, toMoon);
      if (out.lengthSq() < 1e-8) out.set(0, 1, 0);
      else out.normalize();
    }
  }

  _applySky(colors, sunDir) {
    const u = this.domeUniforms;
    _cSky.setHex(colors.sky);
    _cHorizon.setHex(colors.horizon);
    _cSun.setHex(colors.sun);
    u.uSky.value.copy(_cSky);
    u.uHorizon.value.copy(_cHorizon);
    u.uSunColor.value.copy(_cSun);
    u.uSunDir.value.copy(sunDir);
    // Glow nachts schwächer, in der Dämmerung kräftig.
    u.uSunGlow.value = 0.35 + colors.dayness * 0.65 + colors.dusk * 0.4;
  }

  _applyLights(ctx, colors, elevation, sunDir) {
    const focus = this._focusPoint(ctx);

    // Lichtquelle weit in Sonnenrichtung, Target am Spieler.
    _lightPos.copy(focus).addScaledVector(sunDir, STYLE_RULES.shadowDistance * 1.6);
    this.sunLight.position.copy(_lightPos);
    this.sunLight.target.position.copy(focus);
    this.sunLight.target.updateMatrixWorld();

    _cSun.setHex(colors.sun);
    this.sunLight.color.copy(_cSun);
    // Nachts Mondlicht: kühler, schwächer, aber nicht null (Silhouetten bleiben).
    const moonness = clamp01(-elevation * 2);
    if (moonness > 0) {
      _cSun.setHex(PALETTE.moonNight);
      this.sunLight.color.lerp(_cSun, moonness);
    }
    this.sunLight.intensity = colors.sunIntensity;

    // Schatten nur bei genug Höhe — flache Sonne erzeugt Streaking-Artefakte.
    this.sunLight.castShadow = elevation > 0.08;

    _cHemiSky.setHex(colors.sky);
    this.hemi.color.copy(_cHemiSky);
    this.hemi.groundColor.copy(_cGround);
    this.hemi.intensity = colors.ambientIntensity;
  }

  _applyFog(ctx, colors, fogW) {
    _cFog.setHex(colors.fog);
    let fog = ctx.scene.fog;
    if (!fog) {
      fog = new THREE.Fog(colors.fog, colors.fogNear, colors.fogFar);
      ctx.scene.fog = fog;
    }
    fog.color.copy(_cFog);
    // Wetter-Nebel zieht die Far-Plane rein — Konzept §12.1: hinter ~300 m
    // ist Silhouette. Früher fogFar≈207 m bei Nacht (Zug 180 m verschwand).
    // Floor 320 m: Seitenansicht des Zugs bleibt lesbar; Dunst beginnt dahinter.
    const dens = fogW;
    fog.near = colors.fogNear * (1 - dens * 0.40);
    fog.far = Math.max(320, colors.fogFar * (1 - dens * 0.48));
    // Hintergrund leicht Richtung Horizont anheben — nicht dieselbe Instanz wie
    // fog.color, sonst löst sich der Horizont in einen Milchschleier auf.
    _cHorizon.setHex(colors.horizon);
    this._bgColor.copy(_cFog).lerp(_cHorizon, 0.12 + colors.dayness * 0.10);
    ctx.scene.background = this._bgColor;
  }

  _applyExposure(ctx, colors) {
    const r = ctx.renderer;
    if (!r) return;
    // ACESFilmic senkt dunkle Szenen ab. Nachts anheben (lesbare Silhouette),
    // tagsüber nah an 1.0 (knackiges Gegenlicht ohne Ausbrennen).
    // dayness 0 → ~1.35, dayness 1 → ~1.08; Dämmerung +0..0.10.
    r.toneMappingExposure = 1.35 - colors.dayness * 0.27 + colors.dusk * 0.10;
  }

  _applyStars(dayness) {
    const night = clamp01(1 - dayness * 1.4);
    this.stars.material.opacity = night * 0.9;
    this.stars.visible = night > 0.02;
  }

  _applyRain(ctx, rain, wind, dt) {
    // Sichtbar ab 0.15 — darunter nur „Nässe-Stat" ohne Partikel-Pop.
    const show = rain > 0.15;
    this.rain.visible = show;
    if (!show) {
      this.rain.material.opacity = 0;
      return;
    }
    // Weiches Einblenden ab der Schwelle, kein harter Schalter.
    const strength = clamp01((rain - 0.15) / 0.55);
    this.rain.material.opacity = 0.25 + strength * 0.55;
    this.rain.material.size = 0.25 + strength * 0.35;

    const focus = this._focusPoint(ctx);
    // Kamera bevorzugen, falls vorhanden (Regen um die Augen).
    const cx = ctx.camera?.position.x ?? focus.x;
    const cy = ctx.camera?.position.y ?? focus.y + 1.6;
    const cz = ctx.camera?.position.z ?? focus.z;

    const fallSpeed = 14 + rain * 18;
    const windX = (wind - 0.5) * 6;
    const windZ = wind * 3;
    const pos = this._rainPos.array;
    const off = this._rainOffset;
    const fall = this._rainFall;
    const nActive = Math.floor(RAIN_COUNT * (0.35 + strength * 0.65));

    for (let i = 0; i < RAIN_COUNT; i++) {
      if (i >= nActive) {
        // Inaktive Tropfen unter die Welt — bleiben im Buffer, keine Realloc.
        pos[i * 3 + 1] = -999;
        continue;
      }
      fall[i] += dt * fallSpeed * (0.7 + hash01(i, 5) * 0.6);
      if (fall[i] > 1) fall[i] -= Math.floor(fall[i]);

      const ox = off[i * 3] * RAIN_RADIUS;
      const oz = off[i * 3 + 2] * RAIN_RADIUS;
      const oy = (1 - fall[i]) * RAIN_HEIGHT - 2;

      pos[i * 3] = cx + ox + windX * fall[i];
      pos[i * 3 + 1] = cy + oy;
      pos[i * 3 + 2] = cz + oz + windZ * fall[i];
    }
    this._rainPos.needsUpdate = true;
  }

  _focusPoint(ctx) {
    if (ctx.playerPos) {
      _focus.copy(ctx.playerPos);
      return _focus;
    }
    // Vor dem ersten Player-Update: Südbahnhof (CONTRACT / STATIONS[0]).
    const p = trackPoint(STATIONS[0].s);
    _focus.set(p.x, p.y, p.z);
    return _focus;
  }

  lateUpdate(_dt, ctx) {
    // Kuppel und Sterne an der Kamera halten — Parallaxe des Himmels = 0.
    if (!ctx.camera) return;
    const p = ctx.camera.position;
    this.dome.position.copy(p);
    this.stars.position.copy(p);
  }

  dispose() {
    this.dome?.geometry.dispose();
    this.dome?.material.dispose();
    this.stars?.geometry.dispose();
    this.stars?.material.dispose();
    this.rain?.geometry.dispose();
    this.rain?.material.dispose();
  }
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

/** Billiger, stabiler Hash → [0,1). Nur für Regen-Offsets, nicht weltrelevant. */
function hash01(i, salt) {
  let h = (i * 374761393 + salt * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
